// frontend/modules/customer-notification/components/dashboard/NotificationStatusChart.tsx
import React, { useMemo } from 'react';
import { Card, Empty, Spin } from 'antd';
import { NotificationStatistics } from '@/shared/api/model';

interface NotificationStatusChartProps {
    statistics: NotificationStatistics | null;
    isLoading: boolean;
    chartType: 'platform' | 'category';
}

const NotificationStatusChart: React.FC<NotificationStatusChartProps> = ({ 
    statistics, 
    isLoading,
    chartType
}) => {
    const chartData = useMemo(() => {
        if (!statistics) return [];
        
        if (chartType === 'platform') {
            return Object.entries(statistics.notificationsByPlatform || {})
                .map(([platform, count]) => ({
                    name: platform,
                    value: count
                }));
        } else {
            return Object.entries(statistics.notificationsByCategory || {})
                .map(([category, count]) => ({
                    name: formatCategoryName(category),
                    value: count
                }));
        }
    }, [statistics, chartType]);

    const total = useMemo(
        () => chartData.reduce((sum, item) => sum + Number(item.value || 0), 0),
        [chartData]
    );
    
    const getColors = (type: string) => {
        if (type === 'platform') {
            return ['#1890ff', '#13c2c2', '#722ed1', '#eb2f96', '#faad14'];
        } else {
            return ['#52c41a', '#faad14', '#f5222d', '#1890ff', '#722ed1', '#fa8c16', '#a0d911', '#13c2c2', '#eb2f96'];
        }
    };
    
    return (
        <Card title={chartType === 'platform' ? "Notifications by Platform" : "Notifications by Category"} className="h-96">
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : chartData.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
            ) : (
                <div className="space-y-4">
                    {chartData.map((entry, index) => {
                        const value = Number(entry.value || 0);
                        const percent = total > 0 ? (value / total) * 100 : 0;

                        return (
                            <div key={`${entry.name}-${index}`} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                    <span>{entry.name}</span>
                                    <span>{value} ({percent.toFixed(0)}%)</span>
                                </div>
                                <div className="h-3 w-full rounded bg-gray-200 overflow-hidden">
                                    <div
                                        className="h-full rounded"
                                        style={{
                                            width: `${percent}%`,
                                            backgroundColor: getColors(chartType)[index % getColors(chartType).length]
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

// Helper function to format category names
const formatCategoryName = (category: string): string => {
    return category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export default NotificationStatusChart;
